// 011 US1 (P-ORD-1; FR-001, FR-002): the parent's orders with All / Active / Delivered, server paged
// (Show more), refreshed on focus and by pull to refresh on phones. Orders a school placed for their
// child say so. Held in this component's memory only (FR-032).
import { Link, useFocusEffect } from "expo-router";
import { useCallback, useRef, useState } from "react";
import { RefreshControl, ScrollView, StyleSheet, View } from "react-native";

import type { AppError } from "@/api/client";
import type { Api } from "@/api/types";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { formatPaise } from "@/shop/format";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { chipLabel, istDate, ORDERS_COPY as C } from "./copy";
import { type OrdersTab, useOrdersClient } from "./ordersApi";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { flex: 1, backgroundColor: t.color.background },
    page: {
      width: "100%",
      maxWidth: 860,
      alignSelf: "center",
      padding: t.space.lg,
      gap: t.space.md,
      paddingBottom: t.space.x3l,
    },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    chip: {
      minHeight: t.size.button,
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.button,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      justifyContent: "center",
    },
    chipOn: { borderColor: t.color.accent, backgroundColor: t.color.accent },
    chipTextOn: { color: t.color.onAccent },
    card: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.surface,
      padding: t.space.lg,
      gap: t.space.xs,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: t.space.md,
      flexWrap: "wrap",
    },
    status: {
      paddingHorizontal: t.space.sm,
      paddingVertical: t.space.xxs,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.primarySoft,
    },
    statusText: { color: t.color.primaryText },
    action: { marginTop: t.space.xs, alignSelf: "flex-start", minWidth: 160 },
  }),
);

const TABS: OrdersTab[] = ["all", "active", "delivered"];
type Row = Api["ParentOrderRow"];

export function OrdersScreen() {
  const styles = useStyles();
  const wide = useChromeKind() === "desktop";
  const client = useOrdersClient();
  const [tab, setTab] = useState<OrdersTab>("all");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [page, setPage] = useState({ number: 1, totalPages: 1 });
  const [error, setError] = useState<AppError | null>(null);
  const [pulling, setPulling] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Only the latest request may answer: a slow reply for another tab is dropped.
  const latest = useRef(0);

  const load = useCallback(
    async (nextTab: OrdersTab) => {
      const mine = ++latest.current;
      try {
        const p = await client.listOrders(nextTab, 1);
        if (mine !== latest.current) return;
        setRows([...p.items]);
        setPage({ number: 1, totalPages: p.page.totalPages });
        setError(null);
      } catch (e) {
        if (mine === latest.current) setError(e as AppError);
      }
    },
    [client],
  );

  // Focus reloads whichever tab is showing; a tab press loads its own (one request each).
  const tabRef = useRef(tab);
  useFocusEffect(
    useCallback(() => {
      void load(tabRef.current);
    }, [load]),
  );

  async function more() {
    const mine = ++latest.current;
    setLoadingMore(true);
    try {
      const p = await client.listOrders(tab, page.number + 1);
      if (mine !== latest.current) return;
      setRows((r) => [...(r ?? []), ...p.items]);
      setPage({ number: page.number + 1, totalPages: p.page.totalPages });
    } catch (e) {
      if (mine === latest.current) setError(e as AppError);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <ScrollView
      style={styles.scroll}
      testID="orders-screen"
      refreshControl={
        wide ? undefined : (
          <RefreshControl
            refreshing={pulling}
            onRefresh={() => {
              setPulling(true);
              void load(tab).finally(() => setPulling(false));
            }}
          />
        )
      }
    >
      <View style={styles.page} role="main">
        <Text variant="display" accessibilityRole="header" aria-level={1}>
          {C.title}
        </Text>
        <View style={styles.chips} role="radiogroup" aria-label={C.title}>
          {TABS.map((t) => {
            const on = t === tab;
            return (
              <PressableTarget
                key={t}
                accessibilityRole="radio"
                accessibilityLabel={C.tabs[t]}
                aria-checked={on}
                accessibilityState={{ checked: on }}
                onPress={() => {
                  if (t === tab) return;
                  tabRef.current = t;
                  setTab(t);
                  setRows(null);
                  setError(null);
                  void load(t);
                }}
                style={on ? [styles.chip, styles.chipOn] : styles.chip}
              >
                <Text
                  weight="semiBold"
                  style={on ? styles.chipTextOn : undefined}
                >
                  {C.tabs[t]}
                </Text>
              </PressableTarget>
            );
          })}
        </View>
        {error ? (
          <InlineMessage
            tone="error"
            detail={error.requestId ? `Reference: ${error.requestId}` : null}
            action={
              <Button variant="outline" onPress={() => void load(tab)}>
                {C.retry}
              </Button>
            }
          >
            {C.loadFailed}
          </InlineMessage>
        ) : null}
        {rows === null ? (
          error ? null : (
            <Text tone="muted">…</Text>
          )
        ) : rows.length === 0 ? (
          <>
            <Text tone="muted">{C.empty}</Text>
            <View style={styles.action}>
              <Link href="/shop/SCHOOL_BOOKS" asChild>
                <Button variant="outline" accessibilityRole="link">
                  {C.emptyAction}
                </Button>
              </Link>
            </View>
          </>
        ) : (
          rows.map((o) => (
            <View
              key={o.id}
              style={styles.card}
              testID={`order-${o.number}`}
              role="region"
              aria-label={`#${o.number}`}
            >
              <View style={styles.row}>
                <Text weight="bold">{`#${o.number}`}</Text>
                <View style={styles.status}>
                  <Text size="xs" weight="semiBold" style={styles.statusText}>
                    {chipLabel(o.status)}
                  </Text>
                </View>
              </View>
              <Text>{`${C.items(o.itemCount)} · ${o.childNames.join(", ")}`}</Text>
              <Text size="sm" tone="muted">
                {`${o.schoolName} · ${istDate(o.placedAt)}`}
              </Text>
              {o.placedByRole === "SCHOOL" ? (
                <Text size="sm" tone="muted">
                  {C.placedBySchool(o.schoolName)}
                </Text>
              ) : null}
              <View style={styles.row}>
                <Text weight="bold">{formatPaise(o.totalInPaise)}</Text>
                <View style={styles.action}>
                  <Link
                    href={{
                      pathname: "/orders/[orderId]",
                      params: { orderId: o.id },
                    }}
                    asChild
                  >
                    <Button
                      variant={
                        o.status === "OUT_FOR_DELIVERY" ? "primary" : "outline"
                      }
                      accessibilityRole="link"
                      accessibilityHint={`#${o.number}`}
                    >
                      {o.status === "OUT_FOR_DELIVERY" ? C.track : C.view}
                    </Button>
                  </Link>
                </View>
              </View>
            </View>
          ))
        )}
        {rows && page.number < page.totalPages ? (
          <View style={styles.action}>
            <Button
              variant="outline"
              pending={loadingMore}
              onPress={() => void more()}
            >
              {C.more}
            </Button>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}
